import { useEffect, useState } from 'react'
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Box, Button, Text, HStack, VStack, Spinner, Select, IconButton, Badge
} from '@chakra-ui/react'
import { AsyncSelect } from 'chakra-react-select'
import { FaTimes } from 'react-icons/fa'
import { theme } from 'styles'
import { listStorageAccess, grantStorageAccess, revokeStorageAccess, searchStorageUsers } from 'services/storage'
import { errorOf } from './storageUtils'

const userLabel = (u) => {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ')
  return name ? `${name} (${u.username})` : u.username
}

const ROLE_HELP = {
  viewer: 'Can browse and download',
  editor: 'Can also upload, rename, move and delete'
}

// Admin-only: who can see a folder (and everything inside it) and what they
// can do there. Grants on parent folders are shown read-only because they
// already apply here.
const AccessModal = ({ isOpen, onClose, folder, onChanged }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pickedUser, setPickedUser] = useState(null)
  const [pickedRole, setPickedRole] = useState('viewer')
  const [adding, setAdding] = useState(false)
  const [busyUser, setBusyUser] = useState(null)

  const load = async () => {
    setLoading(true)
    const res = await listStorageAccess(folder._id)
    const err = errorOf(res)
    if (err) setError(err)
    else setData(res.data)
    setLoading(false)
  }

  useEffect(() => {
    if (isOpen && folder) {
      setData(null)
      setError(null)
      setPickedUser(null)
      setPickedRole('viewer')
      load()
    }
  }, [isOpen, folder && folder._id]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadOptions = async (input) => {
    const res = await searchStorageUsers(input)
    if (errorOf(res)) return []
    const existing = new Set((data ? data.grants : []).map(g => String(g.user._id)))
    return res.data
      .filter(u => !existing.has(String(u._id)))
      .map(u => ({ value: u._id, label: userLabel(u), user: u }))
  }

  const add = async () => {
    if (!pickedUser) return
    setAdding(true)
    const res = await grantStorageAccess(folder._id, pickedUser.value, pickedRole)
    setAdding(false)
    const err = errorOf(res, 'Could not share')
    if (err) { setError(err); return }
    setError(null)
    setPickedUser(null)
    await load()
    onChanged && onChanged()
  }

  const changeRole = async (grant, role) => {
    setBusyUser(grant.user._id)
    const res = await grantStorageAccess(folder._id, grant.user._id, role)
    setBusyUser(null)
    const err = errorOf(res, 'Could not update')
    if (err) { setError(err); return }
    await load()
  }

  const remove = async (grant) => {
    setBusyUser(grant.user._id)
    const res = await revokeStorageAccess(folder._id, grant.user._id)
    setBusyUser(null)
    const err = errorOf(res, 'Could not remove')
    if (err) { setError(err); return }
    await load()
    onChanged && onChanged()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Share “{folder && folder.name}”</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color={theme.muted} mb={4}>
            People listed here can open this folder and everything inside it using their existing Paloma login.
            Admins always have full access.
          </Text>

          <Box borderWidth="1px" borderColor={theme.line} borderRadius="md" p={3} mb={4} bg={theme.greenTint}>
            <Text fontSize="sm" fontWeight="700" mb={2}>Add a person</Text>
            <HStack align="flex-start" spacing={2} flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
              <Box flex="1" minW="200px">
                <AsyncSelect
                  cacheOptions={false}
                  defaultOptions
                  loadOptions={loadOptions}
                  value={pickedUser}
                  onChange={setPickedUser}
                  placeholder="Search by name, username or email"
                  noOptionsMessage={() => 'No matching users'}
                  size="sm"
                  chakraStyles={{ container: (p) => ({ ...p, bg: 'white' }) }}
                />
              </Box>
              <Select size="sm" w="130px" bg="white" value={pickedRole} onChange={e => setPickedRole(e.target.value)} aria-label="Role">
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </Select>
              <Button size="sm" colorScheme="green" onClick={add} isLoading={adding} isDisabled={!pickedUser}>Add</Button>
            </HStack>
            <Text fontSize="xs" color={theme.muted} mt={2}>{ROLE_HELP[pickedRole]}.</Text>
          </Box>

          {loading && <Box textAlign="center" py={4}><Spinner /></Box>}
          {data && (
            <VStack align="stretch" spacing={2}>
              {data.grants.length === 0 && (
                <Text fontSize="sm" color={theme.muted} py={2}>Nobody has been given access to this folder yet.</Text>
              )}
              {data.grants.map(g => (
                <HStack key={g._id} p={2} borderWidth="1px" borderColor={theme.line} borderRadius="md" spacing={3}>
                  <Box flex="1" minW={0}>
                    <Text fontSize="sm" fontWeight="600" noOfLines={1}>{userLabel(g.user)}</Text>
                    <Text fontSize="xs" color={theme.muted} noOfLines={1}>
                      {g.user.email}{g.user.user_type ? ` · ${g.user.user_type}` : ''}
                    </Text>
                  </Box>
                  <Select
                    size="sm" w="110px" value={g.role}
                    onChange={e => changeRole(g, e.target.value)}
                    isDisabled={busyUser === g.user._id}
                    aria-label={`Role for ${g.user.username}`}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </Select>
                  <IconButton
                    size="sm" variant="ghost" colorScheme="red" aria-label={`Remove ${g.user.username}`}
                    icon={<FaTimes />} onClick={() => remove(g)} isLoading={busyUser === g.user._id}
                  />
                </HStack>
              ))}
              {data.inherited.length > 0 && (
                <Box pt={2}>
                  <Text fontSize="xs" fontWeight="700" color={theme.muted} textTransform="uppercase" letterSpacing="0.04em" mb={1}>
                    Also has access through a parent folder
                  </Text>
                  {data.inherited.map(g => (
                    <HStack key={g._id} px={2} py={1} spacing={2}>
                      <Text fontSize="sm" noOfLines={1}>{userLabel(g.user)}</Text>
                      <Badge colorScheme={g.role === 'editor' ? 'green' : 'gray'} textTransform="capitalize">{g.role}</Badge>
                      <Text fontSize="xs" color={theme.muted}>via “{g.folder.name}”</Text>
                    </HStack>
                  ))}
                </Box>
              )}
            </VStack>
          )}
          {error && <Text mt={3} fontSize="sm" color={theme.danger}>{error}</Text>}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="green" onClick={onClose}>Done</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AccessModal
